// ── Auth contract (passwordless email OTP) ──────────────────────────────────
// LIVE backend integration (swapped from the mock-first build).
//
// Login is passwordless: the citizen enters an email, the backend emails a
// one-time code, and the code is exchanged for a session token. Backend owns:
// account lookup, OTP generation/expiry/rate-limiting, email delivery, and token
// issuance.

import { apiClient } from 'src/api/client';

export { applyAuthToken, redirectToPortalHome } from './session';

// No longer thrown by this module since the live-backend swap; retained only
// for existing consumers and test doubles (EmailCodeLogin.test.tsx,
// EmailLoginSurface.test.tsx mock it).
export class AuthError extends Error {}

/**
 * Why a code is being asked for. The login surface sends none (invite-only:
 * the backend emails a code only when the address is a PropertyContact). The
 * skip-hire request door (PRODUCT-485) sends `customer_request` so a backend
 * with the door enabled can mint an identity for an email it does not know
 * yet (BE-P3-1). Additive — a backend that predates the field ignores it.
 */
export type OtpPurpose = 'customer_request';

export async function requestOtp(input: {
  email: string;
  purpose?: OtpPurpose;
}): Promise<void> {
  // Always resolves (neutral 200) — never reveals whether the account exists.
  // The backend only emails a code when the address maps to a real account.
  await apiClient.post('auth/request-otp', {
    email: input.email,
    ...(input.purpose ? { purpose: input.purpose } : {}),
  });
}

export async function verifyOtp(input: {
  email: string;
  code: string;
}): Promise<{ token: string }> {
  return (await apiClient.post('auth/verify-otp', input)).data;
}
